import { Box, Checkbox, TabBar, ToggleGroup } from '@eatclubapp/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { TextField } from '../../../../components/TextField';
import { Typography } from '../../../../components/Typography';
import { updateBookingAction } from '../../../../data/actions/bookingAction';
import { bookingKeys, bookingPropTypes } from '../../../../data/models/Booking';
import { dietaryTagPropTypes, dietaryTagsPropTypes } from '../../../../data/models/DietaryTag';
import { restaurantPropTypes } from '../../../../data/models/Restaurant';
import { isEmpty, range, trim } from '../../../../utils/helpers';
import useStyles from './DietaryRequirementsStyles';

const DietaryRequirements = ({
  style,
  booking,
  restaurant,
  updateBooking,
  dietaryTags,
  mandatory,
  dietaryTagsMandatory,
  dietaryErrors,
  defaultTab, // Used to force a specific tab e.g. for errors or when editing
}) => {
  const theme = useTheme();
  const classes = useStyles({ style });

  const [activeTab, setActiveTab] = useState(defaultTab);

  const [guestDietaries, setGuestDietaries] = useState(
    booking?.dietaryRequirements?.map((requirement, index) => ({
      ...requirement,
      index,
      noDietaries: requirement?.noDietaries ?? false,
    })),
  );

  const message = restaurant?.data?.dietaryMessage;

  const dietaryOptions = [
    ...dietaryTags.data.map((dietaryTag) => ({
      label: dietaryTag.dietaryTag,
      value: dietaryTag.id,
    })),
    {
      value: 'other',
      label: '+ Add notes',
    },
  ];

  const pillStyles = {
    option: {
      boxSizing: 'border-box',
      padding: '0 11px',
      borderRadius: '20px',
      fontSize: '15px',
      lineHeight: '15px',
      minWidth: '50px',
      height: '35px',
      transition: 'background-color 0.1s',
      whiteSpace: 'nowrap',
      alignItems: 'center',
      backgroundColor: theme.colors.dietaryTagBackgroundColor,
      border: `1px solid ${theme.colors.dietaryTagBorderColor}`,
      borderColor: theme.colors.dietaryTagBorderColor,
      color: theme.colors.dietaryTagTextColor,
    },
    selectedOption: {
      fontWeight: theme.fontWeights.regular,
      padding: '9px 11px',
      paddingBottom: '9px',
      backgroundColor: theme.colors.dietaryTagActiveBackgroundColor,
      border: `1px solid ${theme.colors.dietaryTagActiveBorderColor}`,
      borderColor: theme.colors.dietaryTagActiveBorderColor,
      color: theme.colors.dietaryTagActiveTextColor,
    },
    hoveredOption: {
      // border: `1px solid ${theme.colors.dietaryTagActiveBorderColor}`,
      filter: 'brightness(95%)',
    },
  };

  const tabBarStyles = {
    // marginLeft: '-6px', // So the text sits flush with the heading
    containerInner: {
      gap: '12px',
      flexWrap: 'wrap',
    },
    tab: {
      boxSizing: 'border-box',
      padding: '0 11px',
      // marginLeft: '-11px',
      borderRadius: '8px',
      fontSize: '15px',
      lineHeight: '15px',
      minWidth: '50px',
      transition: 'background-color 0.1s',
      whiteSpace: 'nowrap',
      alignItems: 'center',
      display: 'flex',
      gap: '10px',
      border: '1px solid transparent',
      fontWeight: theme.fontWeights.medium,
      color: theme.colors.dietaryTagTextColor,
      backgroundColor: 'transparent',
    },
    tabActive: {
      fontWeight: theme.fontWeights.medium,
      padding: '9px 11px',
      paddingBottom: '9px',
      backgroundColor: theme.colors.dietaryTagActiveBackgroundColor,
      color: theme.colors.dietaryTagActiveTextColor,
      border: `1px solid ${theme.colors.dietaryTagActiveBorderColor}`,
      borderBottom: `1px solid ${theme.colors.dietaryTagActiveBorderColor}`, // defaults to red
    },
    tabHover: {
      backgroundColor: 'transparent',
      color: theme.colors.black,
      filter: 'none',
    },
  };

  const getLabel = (guestIndex) => {
    const hasErrors = dietaryErrors?.some((error) => error?.index === guestIndex);

    return (
      <div>
        {(guestIndex === 0 && booking?.[bookingKeys.firstName]) || `Guest ${guestIndex + 1}`}
        {hasErrors ? <span style={{ color: theme.colors.redOrange }}>*</span> : ''}
      </div>
    );
  };

  const guestLabels = useMemo(() => {
    return range(0, booking.guests - 1).map((guestIndex) => {
      return {
        value: guestIndex,
        label: getLabel(guestIndex),
        // label: <span>'Hello'</span>,
      };
    });
  }, [booking.guests, booking?.[bookingKeys.firstName], dietaryErrors]);

  // Set guest dietaries on initial load
  useEffect(() => {
    const newDietaries = range(0, booking.guests - 1).map((guestIndex) => ({
      name: guestDietaries?.[guestIndex]?.name || null,
      dietaries: guestDietaries?.[guestIndex]?.dietaries || [], // list of strings for chosen dietary options
      noDietaries: guestDietaries?.[guestIndex]?.noDietaries || false, // list of strings for chosen dietary options
      dietaryNotes: guestDietaries?.[guestIndex]?.dietaryNotes, // custom notes from the guest
    }));

    setGuestDietaries(newDietaries);
  }, [booking.guests]);

  // Switch to the first guest with errors if there are any
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const updateGuestDietary = (index, newValues) => {
    const newDietary = { ...guestDietaries?.[index], ...newValues };
    const newDietaries = guestDietaries.map((dietary, guestIndex) => {
      if (guestIndex !== index) {
        return dietary;
      }

      return newDietary;
    });

    setGuestDietaries(newDietaries);

    updateBooking('dietaryRequirements', newDietaries);
  };

  const handleToggleOption = (guestIndex, selectedOption) => {
    updateGuestDietary(guestIndex, { dietaries: selectedOption });
  };

  const hasErrors = mandatory && dietaryErrors?.some((error) => error?.index === activeTab);

  return (
    <Box className={classes.wrapper}>
      {/* More dangerous stuff */}
      <div dangerouslySetInnerHTML={{ __html: message }} />
      <TabBar tabs={guestLabels} value={activeTab} onSelect={setActiveTab} style={tabBarStyles} />

      <Box className={classes.tabContent}>
        {activeTab > 0 && (
          <>
            <TextField
              label='Full name'
              value={guestDietaries?.[activeTab]?.name || ''}
              onChange={(e) => updateGuestDietary(activeTab, { name: e.target.value })}
              InputProps={{ disableUnderline: true }}
              // autoComplete='given-name'
              // name='firstName'
              required={mandatory}
              error={hasErrors && isEmpty(trim(guestDietaries?.[activeTab]?.name))}
              // helperText={
              //   hasErrors &&
              //   isEmpty(trim(guestDietaries?.[activeTab]?.name)) && (
              //     <Typography variant='caption' style={{ paddingTop: '4px' }}>
              //       This field is required
              //     </Typography>
              //   )
              // }
            />
          </>
        )}

        {/* {hasErrors && */}
        {/*   isEmpty(guestDietaries?.[activeTab]?.dietaries) && */}
        {/*   !guestDietaries?.[activeTab]?.noDietaries && ( */}
        {/*     <Typography variant='caption' style={{ color: theme.colors.redOrange }}> */}
        {/*       Please select the dietaries for this guest, or select No dietaries for this guest */}
        {/*     </Typography> */}
        {/*   )} */}

        {dietaryTagsMandatory && (
          <Checkbox
            label={<Typography variant='small'>No dietaries for this guest</Typography>}
            color={theme.colors.charcoal}
            onChange={(selected) =>
              updateGuestDietary(activeTab, { noDietaries: selected, dietaries: [] })
            }
            selected={guestDietaries?.[activeTab]?.noDietaries}
            style={{ checkboxBox: { backgroundColor: 'transparent' } }}
          />
        )}

        {!guestDietaries?.[activeTab]?.noDietaries && (
          <Box className={classes.pillContainer}>
            <ToggleGroup
              items={dietaryOptions}
              style={pillStyles}
              value={guestDietaries?.[activeTab]?.dietaries}
              onSelect={(newValue) => handleToggleOption(activeTab, newValue)}
              singleChoice={false}
              showCheckmark={false}
              disabled={false}
            />

            {guestDietaries?.[activeTab]?.dietaries?.includes('other') && (
              <TextField
                label='Dietary notes'
                value={guestDietaries?.[activeTab]?.dietaryNotes || ''}
                onChange={(e) => updateGuestDietary(activeTab, { dietaryNotes: e.target.value })}
                InputProps={{ disableUnderline: true }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

DietaryRequirements.defaultProps = {
  style: {},
  mandatory: false,
  dietaryTagsMandatory: false,
  dietaryErrors: [],
  defaultTab: 0,
};

DietaryRequirements.propTypes = {
  booking: bookingPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  style: PropTypes.shape({}),
  updateBooking: PropTypes.func.isRequired,
  dietaryTags: dietaryTagsPropTypes.isRequired,
  mandatory: PropTypes.bool,
  dietaryTagsMandatory: PropTypes.bool,
  dietaryErrors: PropTypes.arrayOf(dietaryTagPropTypes),
  defaultTab: PropTypes.number,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  restaurant: state.restaurant,
  dietaryTags: state.dietaryTags,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateBooking: updateBookingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(DietaryRequirements);
